import React from 'react';
import { CTASection } from '../../components/CTASection';
import { FAQSection } from '../../components/FAQSection';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { TrendingUp, FileText, AlertCircle, Activity, Stethoscope, Clock, IndianRupee } from 'lucide-react';
import { useSite } from '../../contexts/SiteContext';

export const CalcificTendonitis = () => {
  const { theme, doctor } = useSite();
  
  const faqs = [
    { question: 'What causes calcific tendonitis?', answer: 'Calcium deposits form within the rotator cuff tendons (most commonly supraspinatus). The exact cause is unknown but may relate to local tissue hypoxia, degeneration, or metabolic factors. It is NOT related to dietary calcium intake. Women aged 30-60 are most commonly affected.' },
    { question: 'Will the calcium dissolve on its own?', answer: 'Yes, in many cases. Calcific tendonitis has a natural history of resolution over 1-3 years as the body reabsorbs the calcium during the resorptive phase. However, this phase can be very painful. Treatment aims to manage symptoms during this process.' },
    { question: 'What is barbotage and how does it work?', answer: 'Barbotage (ultrasound-guided lavage) is a minimally invasive procedure where a needle is inserted into the calcium deposit under ultrasound guidance. Saline is used to break up and aspirate the calcium. It has 60-75% success rate and can be done as an outpatient procedure in Hyderabad.' },
    { question: 'How much does calcific tendonitis treatment cost in Hyderabad?', answer: 'Conservative treatment: ₹2,000-5,000 per session. Barbotage procedure: ₹15,000-25,000. Shockwave therapy: ₹3,000-5,000 per session (4-6 sessions). Arthroscopic surgery: ₹1.5-2.5 lakhs including hospital stay. Insurance often covers surgical treatment.' },
    { question: 'When is surgery needed for calcific tendonitis?', answer: 'Surgery is considered when conservative treatment fails after 6+ months, severe pain affects quality of life, or the deposit is in the chronic formative phase (unlikely to resorb). Arthroscopic removal is highly effective with 90%+ success rate.' },
    { question: 'What is the recovery time after barbotage?', answer: 'After barbotage, most patients experience significant pain relief within 1-2 weeks. You can return to normal activities within a few days. Some soreness at the injection site is common for 24-48 hours. Full recovery typically occurs within 4-6 weeks.' }
  ];

  return (
    <div className="min-h-screen" data-testid="calcific-tendonitis-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-orange-50 to-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <span className="inline-block px-4 py-1 rounded-full text-sm font-medium mb-4" style={{ backgroundColor: `${theme?.primaryHex}20`, color: theme?.primaryHex }}>
              Degenerative & Wear
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Calcific Tendonitis Treatment in Hyderabad</h1>
            <p className="text-xl text-gray-600">Barbotage, shockwave therapy & arthroscopic removal by Dr. Deepthi Nandan Reddy at Apollo Hospitals</p>
          </div>
        </div>
      </section>

      {/* What is Calcific Tendonitis */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">What is Calcific Tendonitis?</h2>
            <div className="prose prose-lg max-w-none text-gray-700 space-y-4">
              <p>Calcific tendonitis (also called calcific tendinopathy or calcifying tendinitis) is a condition where calcium deposits form within the rotator cuff tendons of the shoulder. These deposits can cause severe shoulder pain, especially during the resorptive phase when the body attempts to break down the calcium.</p>
              <p><strong>Epidemiology:</strong> Calcific tendonitis affects 2-7% of adults, with the highest prevalence in women aged 30-60 years. The supraspinatus tendon is involved in approximately 80% of cases, followed by the infraspinatus and subscapularis tendons.</p>
              <p><strong>Key Point:</strong> Unlike other forms of tendon degeneration, calcific tendonitis often has a self-limiting course. Many cases resolve spontaneously over 1-3 years, though this process can be extremely painful.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Symptoms */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <Activity className="w-8 h-8" style={{ color: theme?.primaryHex }} />
              <h2 className="text-3xl font-bold text-gray-900">Symptoms of Calcific Tendonitis</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader><CardTitle className="flex items-center gap-2"><AlertCircle className="w-5 h-5" style={{ color: theme?.primaryHex }} />Acute Phase Symptoms</CardTitle></CardHeader>
                <CardContent className="text-gray-700 space-y-2">
                  <p>• Sudden, severe shoulder pain (often described as excruciating)</p>
                  <p>• Pain that wakes you from sleep</p>
                  <p>• Inability to move the shoulder due to pain</p>
                  <p>• Tenderness over the affected tendon</p>
                  <p>• Pain radiating down the arm</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader><CardTitle className="flex items-center gap-2"><AlertCircle className="w-5 h-5" style={{ color: theme?.primaryHex }} />Chronic Phase Symptoms</CardTitle></CardHeader>
                <CardContent className="text-gray-700 space-y-2">
                  <p>• Dull, aching shoulder pain</p>
                  <p>• Pain with overhead activities</p>
                  <p>• Shoulder stiffness</p>
                  <p>• Impingement-like symptoms</p>
                  <p>• Night pain (less severe than acute phase)</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Phases of Calcific Tendonitis */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Phases of Calcific Tendonitis</h2>
            <div className="space-y-4">
              <Card className="border-l-4 border-l-yellow-500">
                <CardHeader><CardTitle>1. Pre-Calcific Stage</CardTitle></CardHeader>
                <CardContent className="text-gray-700">
                  <p>Fibrocartilaginous transformation of tendon tissue. Usually asymptomatic. Site predisposed to calcium deposition.</p>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-blue-500">
                <CardHeader><CardTitle>2. Formative Phase</CardTitle></CardHeader>
                <CardContent className="text-gray-700">
                  <p>Calcium crystals deposit within the tendon. Pain may be mild or absent. Deposit appears chalky and well-defined on X-ray. Can last months to years.</p>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-red-500">
                <CardHeader><CardTitle>3. Resorptive Phase (Most Painful)</CardTitle></CardHeader>
                <CardContent className="text-gray-700">
                  <p>Body attempts to reabsorb calcium through inflammatory process. Causes severe, acute pain. Deposit becomes soft and toothpaste-like. This is when most patients seek treatment in Hyderabad.</p>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-green-500">
                <CardHeader><CardTitle>4. Post-Calcific Stage</CardTitle></CardHeader>
                <CardContent className="text-gray-700">
                  <p>Calcium reabsorbed. Tendon heals with collagen remodeling. Pain resolves. Full recovery occurs.</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Diagnosis */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <Stethoscope className="w-8 h-8" style={{ color: theme?.primaryHex }} />
              <h2 className="text-3xl font-bold text-gray-900">Diagnosis</h2>
            </div>
            <div className="prose prose-lg max-w-none text-gray-700 space-y-4">
              <p><strong>X-ray:</strong> The gold standard for diagnosis. Calcium deposits appear as white opacities within the tendon. X-rays can also help determine the phase (formative deposits are well-defined; resorptive deposits are fluffy/ill-defined).</p>
              <p><strong>Ultrasound:</strong> Excellent for visualizing calcium deposits and guiding treatment (barbotage). Can detect deposits as small as 2-3mm. Also useful for assessing surrounding structures.</p>
              <p><strong>MRI:</strong> Not always necessary but helpful to exclude other pathology like rotator cuff tears. May show bone marrow edema in the humeral head during acute phase.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Treatment Options */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <TrendingUp className="w-8 h-8" style={{ color: theme?.primaryHex }} />
              <h2 className="text-3xl font-bold text-gray-900">Treatment Options in Hyderabad</h2>
            </div>
            <div className="space-y-6">
              <Card className="border-l-4 border-l-green-500">
                <CardHeader><CardTitle>Conservative Treatment (First Line)</CardTitle></CardHeader>
                <CardContent className="text-gray-700 space-y-2">
                  <p>• <strong>NSAIDs:</strong> For pain and inflammation control</p>
                  <p>• <strong>Physiotherapy:</strong> Maintain range of motion, avoid impingement positions</p>
                  <p>• <strong>Corticosteroid injection:</strong> Subacromial injection for acute pain relief</p>
                  <p>• <strong>Ice and rest:</strong> Activity modification during acute flares</p>
                  <p className="text-sm text-gray-500 mt-3 bg-green-50 p-3 rounded">Success rate: 60-70% of cases resolve with conservative measures over 1-2 years</p>
                </CardContent>
              </Card>

              {/* BARBOTAGE - Detailed Section */}
              <Card className="border-l-4 border-l-blue-500 bg-blue-50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <span className="bg-blue-600 text-white text-xs px-2 py-1 rounded">RECOMMENDED</span>
                    Barbotage Procedure (Ultrasound-Guided Lavage)
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-gray-700 space-y-4">
                  <p>Barbotage is a minimally invasive, image-guided procedure that offers faster relief than waiting for natural resorption. Dr. Deepthi Nandan Reddy performs this procedure at Apollo Hospitals, Financial District, Hyderabad.</p>
                  
                  <div className="bg-white p-4 rounded-lg">
                    <p className="font-semibold mb-2">How Barbotage Works:</p>
                    <p>1. <strong>Local anesthesia</strong> is administered to numb the shoulder</p>
                    <p>2. Under <strong>real-time ultrasound guidance</strong>, a needle is precisely inserted into the calcium deposit</p>
                    <p>3. <strong>Saline solution</strong> is injected to break up and fragment the calcium</p>
                    <p>4. The <strong>calcium slurry is aspirated</strong> (removed) through the needle</p>
                    <p>5. A <strong>corticosteroid injection</strong> may be given to reduce inflammation</p>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4 mt-4">
                    <div className="bg-white p-4 rounded-lg">
                      <p className="font-semibold mb-2">Advantages of Barbotage:</p>
                      <p>• Outpatient procedure (30-45 minutes)</p>
                      <p>• No general anesthesia needed</p>
                      <p>• Immediate return to daily activities</p>
                      <p>• 60-75% success rate</p>
                      <p>• Can avoid surgery in most cases</p>
                    </div>
                    <div className="bg-white p-4 rounded-lg">
                      <p className="font-semibold mb-2">What to Expect After:</p>
                      <p>• Mild soreness for 24-48 hours</p>
                      <p>• Pain relief within 1-2 weeks</p>
                      <p>• Return to work: 1-2 days</p>
                      <p>• Full recovery: 4-6 weeks</p>
                      <p>• May need repeat procedure in 10-15%</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-l-4 border-l-purple-500">
                <CardHeader><CardTitle>Extracorporeal Shockwave Therapy (ESWT)</CardTitle></CardHeader>
                <CardContent className="text-gray-700 space-y-2">
                  <p>High-energy acoustic waves are directed at the calcium deposit to fragment it and promote resorption.</p>
                  <p>• <strong>Sessions:</strong> 4-6 treatments, 1 week apart</p>
                  <p>• <strong>Success rate:</strong> 70-80% improvement</p>
                  <p>• <strong>Best for:</strong> Formative phase deposits, patients preferring non-invasive treatment</p>
                  <p>• <strong>Note:</strong> May cause temporary increase in pain</p>
                </CardContent>
              </Card>

              <Card className="border-l-4 border-l-orange-500">
                <CardHeader><CardTitle>Arthroscopic Surgery (If Conservative Fails)</CardTitle></CardHeader>
                <CardContent className="text-gray-700 space-y-2">
                  <p>Minimally invasive keyhole surgery to remove calcium deposits under direct visualization.</p>
                  <p>• <strong>Indications:</strong> Failed conservative/barbotage treatment after 6+ months</p>
                  <p>• <strong>Procedure:</strong> 2-3 small incisions, camera-guided calcium removal</p>
                  <p>• <strong>May combine with:</strong> Subacromial decompression if impingement present</p>
                  <p>• <strong>Success rate:</strong> 90%+ pain relief</p>
                  <p>• <strong>Recovery:</strong> Sling 1-2 weeks, physio 6-12 weeks, full recovery 3-4 months</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Cost in Hyderabad */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <IndianRupee className="w-8 h-8" style={{ color: theme?.primaryHex }} />
              <h2 className="text-3xl font-bold text-gray-900">Cost of Treatment in Hyderabad</h2>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <p className="text-gray-600 mb-4">Indicative costs at Apollo Hospitals Financial District (may vary based on individual case complexity):</p>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="text-gray-700">Consultation with Dr. Reddy</span>
                  <span className="font-semibold">₹800-1,500</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="text-gray-700">X-ray + Ultrasound</span>
                  <span className="font-semibold">₹1,500-3,000</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="text-gray-700">Corticosteroid Injection</span>
                  <span className="font-semibold">₹2,000-4,000</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="text-gray-700">Barbotage Procedure</span>
                  <span className="font-semibold">₹15,000-25,000</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="text-gray-700">Shockwave Therapy (per session)</span>
                  <span className="font-semibold">₹3,000-5,000</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-700">Arthroscopic Surgery (all-inclusive)</span>
                  <span className="font-semibold">₹1.5-2.5 Lakhs</span>
                </div>
              </div>
              <p className="text-sm text-gray-500 mt-4">Most insurance policies cover surgical treatment. Check with your provider for specific coverage.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Recovery Timeline */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <Clock className="w-8 h-8" style={{ color: theme?.primaryHex }} />
              <h2 className="text-3xl font-bold text-gray-900">Recovery Timeline</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <Card>
                <CardHeader><CardTitle className="text-green-700">Conservative Treatment</CardTitle></CardHeader>
                <CardContent className="text-gray-700 space-y-2">
                  <p>• <strong>Week 1-2:</strong> Pain management</p>
                  <p>• <strong>Week 2-6:</strong> Gradual activity increase</p>
                  <p>• <strong>Month 2-6:</strong> Progressive strengthening</p>
                  <p>• <strong>Month 6-24:</strong> Natural resolution</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader><CardTitle className="text-blue-700">After Barbotage</CardTitle></CardHeader>
                <CardContent className="text-gray-700 space-y-2">
                  <p>• <strong>Day 1-2:</strong> Local soreness</p>
                  <p>• <strong>Week 1-2:</strong> Significant pain relief</p>
                  <p>• <strong>Week 2-4:</strong> Return to full activities</p>
                  <p>• <strong>Week 4-6:</strong> Complete recovery</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader><CardTitle className="text-orange-700">After Surgery</CardTitle></CardHeader>
                <CardContent className="text-gray-700 space-y-2">
                  <p>• <strong>Week 1-2:</strong> Sling, gentle motion</p>
                  <p>• <strong>Week 2-6:</strong> Range of motion exercises</p>
                  <p>• <strong>Week 6-12:</strong> Strengthening program</p>
                  <p>• <strong>Month 3-4:</strong> Full recovery</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Scientific References */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <FileText className="w-6 h-6 text-gray-500" />
              <h3 className="text-xl font-bold text-gray-700">Scientific References</h3>
            </div>
            <div className="bg-white p-6 rounded-lg text-sm text-gray-600 space-y-2">
              <p>1. Louwerens JK, et al. Calcific tendinitis of the rotator cuff: A systematic review. J Shoulder Elbow Surg. 2014</p>
              <p>2. Chiou HJ, et al. Ultrasound-guided percutaneous lavage for calcific tendinopathy of the rotator cuff. Skeletal Radiol. 2014</p>
              <p>3. Verstraelen FU, et al. High-energy versus low-energy extracorporeal shockwave therapy for calcifying tendinitis. J Bone Joint Surg Am. 2014</p>
              <p className="text-xs text-gray-400 mt-4 pt-4 border-t">Last Updated: January 2025 | Content reviewed by Dr. Deepthi Nandan Reddy, FRCS (Ortho)</p>
            </div>
          </div>
        </div>
      </section>

      <FAQSection faqs={faqs} />
      <CTASection />
    </div>
  );
};

export default CalcificTendonitis;
